import { ComandaSplit } from '@/enums/ComandaSplit';

/**
 * Data for the item modal's comanda-layout preview: a fixed example order
 * rendered as mini tickets exactly as the printer produces them in each
 * `comandaSplit` mode. Pure and i18n-key based; the component renders it.
 */

export interface IComandaPreviewLine {
  /** Units on this ticket line, rendered as `2x`. */
  quantity: number;
  /** i18n key of the placeholder name ("other product"); null = the product being configured (rendered bold, with its real name). */
  nameKey: string | null;
  /** Rendered after the name, e.g. `(1/2)`; null when none. */
  suffix: string | null;
  /** i18n key of the small note under the line (`sameOptions` / `otherOption`); null when none. */
  noteKey: string | null;
}

export interface IComandaPreviewTicket {
  /** i18n key of the ticket caption (`wholeOrder` / `restOfOrder`); null for the product's own tickets. */
  captionKey: string | null;
  lines: IComandaPreviewLine[];
}

export interface IComandaSplitPreview {
  tickets: IComandaPreviewTicket[];
  /** i18n key of the one-sentence result under the tickets. */
  resultKey: string;
}

const KEY = 'item.comandaSplitPreview.';
const SAME = `${KEY}sameOptions`;
const OTHER_OPTION = `${KEY}otherOption`;

/**
 * Fixed example: 3 units of the product ordered, 2 identical and 1 with
 * another option (= two order lines, amounts 2 and 1, because the POS cart
 * merges identical configurations into one line), plus 2 of another product.
 * Mirrors what the printer produces in each mode.
 */
export function buildComandaSplitPreview(
  mode: ComandaSplit
): IComandaSplitPreview {
  const other: IComandaPreviewLine = {
    quantity: 2,
    nameKey: `${KEY}otherProduct`,
    suffix: null,
    noteKey: null,
  };
  const product = (
    quantity: number,
    suffix: string | null,
    noteKey: string
  ): IComandaPreviewLine => ({ quantity, nameKey: null, suffix, noteKey });
  const rest: IComandaPreviewTicket = {
    captionKey: `${KEY}restOfOrder`,
    lines: [other],
  };

  switch (mode) {
    case ComandaSplit.Unit:
      return {
        tickets: [
          rest,
          { captionKey: null, lines: [product(1, '(1/2)', SAME)] },
          { captionKey: null, lines: [product(1, '(2/2)', SAME)] },
          { captionKey: null, lines: [product(1, '(1/1)', OTHER_OPTION)] },
        ],
        resultKey: `${KEY}resultUnit`,
      };
    case ComandaSplit.Line:
      return {
        tickets: [
          rest,
          { captionKey: null, lines: [product(2, null, SAME)] },
          { captionKey: null, lines: [product(1, null, OTHER_OPTION)] },
        ],
        resultKey: `${KEY}resultLine`,
      };
    default:
      return {
        tickets: [
          {
            captionKey: `${KEY}wholeOrder`,
            lines: [
              other,
              product(2, null, SAME),
              product(1, null, OTHER_OPTION),
            ],
          },
        ],
        resultKey: `${KEY}resultNone`,
      };
  }
}
